import { useEffect } from 'react';
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { useSelfServiceAuth } from '@/stores/selfServiceAuth';
import { useBranding } from '@/hooks/useBranding';
import { useTenantID } from '@/hooks/useTenantID';
import { useFavicon } from '@/hooks/useFavicon';
import { useSelfServiceTokenRefresh } from '@/hooks/useSelfServiceTokenRefresh';
import Button from '@/components/core/Button';
import { IconLogout } from '@/icons';
import { cn } from '@/helpers/classnames';

const publicPaths = ['/login', '/auth/verify'];

export const Route = createFileRoute('/$tenantId/self-service/_layout')({
  component: SelfServiceLayout,
  beforeLoad: async ({ location, params }) => {
    const isPublicPath = publicPaths.some((p) => location.pathname.endsWith(p));
    if (isPublicPath) return;

    const { isTokenValid } = useSelfServiceAuth.getState();
    if (isTokenValid()) return;

    throw redirect({
      to: '/$tenantId/self-service/login',
      params: { tenantId: params.tenantId },
    });
  },
});

function SelfServiceLayout() {
  const tenantId = useTenantID();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useSelfServiceAuth((state) => state.logout);
  const isTokenValid = useSelfServiceAuth((state) => state.isTokenValid);

  useFavicon(tenantId);
  useSelfServiceTokenRefresh();

  useEffect(() => {
    const isPublicPath = publicPaths.some((p) => location.pathname.endsWith(p));
    if (isPublicPath) return;

    // Subscribe to store changes for external updates (e.g., 401 interceptor)
    const unsubscribe = useSelfServiceAuth.subscribe((state) => {
      if (!state.isTokenValid()) {
        navigate({ to: '/$tenantId/self-service/login', params: { tenantId } });
      }
    });

    return unsubscribe;
  }, [navigate, tenantId, location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const showLogout = isTokenValid();

  return (
    <div className={cn('flex min-h-screen w-full flex-col', tenantId)}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-background-50 focus:p-4 focus:text-primary-700"
      >
        <FormattedMessage defaultMessage="Hoppa till huvudinnehåll" />
      </a>
      <header className="flex items-center justify-between border-b border-background-300 px-4 py-3">
        <div className="h-12 w-32">{branding?.logo}</div>
        {showLogout && (
          <Button
            variant="ghost"
            onClick={handleLogout}
            to="/$tenantId/self-service/login"
            startIcon={<IconLogout />}
          >
            <FormattedMessage defaultMessage="Logga ut" />
          </Button>
        )}
      </header>
      <main id="main-content" className="flex grow flex-col">
        <Outlet />
      </main>
    </div>
  );
}
